import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonIcon,
  IonCard,
  IonCardContent,
  IonRouterLink,
} from '@ionic/react';
import { arrowForwardOutline } from 'ionicons/icons';
import {
  Box,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import useFetch from 'use-http';
import PropTypes from 'prop-types';
import LoadingSpinner from 'components/generic/LoadingSpinner';
import { useAuthContext } from 'providers/auth-provider';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Goal from 'pages/protege/goals/Goal';
import './Goals.css';
import appStrings from 'common/app_strings';
import FilterBar from 'common/atoms/filterBar/FilterBar';
import NoGoals from 'pages/NoGoals';
import { toast } from 'react-hot-toast';
import { filters } from './utils';
import { useUserPermissionsQuery } from '../../../redux/apis/permissions.api';
import USER_PERMISSIONS from '../../../permissions/user.permissions';

const { CRUD_GOALS } = USER_PERMISSIONS;
const { goals: strings } = appStrings;

/**
 *  This component has been refactored to act as the 'Goal' tab component
 *  It is the client users own goals management screen.
 *
 * TODO: rename and move this file away from the 'protege' directory/namespace.  It should live with other goal related screens.
 */
const Goals = ({ history }) => {
  const { user } = useAuthContext();
  const { data: userPermissions } = useUserPermissionsQuery({ user }, { skip: !user });
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { del, patch } = useFetch();
  const { get: fetchGoals, loading } = useFetch(`/goals/user/${user?.id}`);

  const handleFilteredGoals = (type) => {
    if (!selectedFilter) setFilteredGoals(goals);
    else {
      const updatedGoals = goals.filter((goal) => goal.type === type);
      setFilteredGoals(updatedGoals);
    }
  };

  const handleGoalDelete = (goal) => {
    del(`/goals/${goal.id}`);
    const updatedGoals = goals.filter((currentGoal) => currentGoal.id !== goal.id);
    setGoals(updatedGoals);
    toast.success('Goal deleted.');
  };

  const handleGoalEdit = (goal) => {
    history.push('/goals-edit', { currentGoal: goal });
  };

  const handleGoalCelebration = (goal) => {
    history.push('/goals/celebration', goal);
  };

  const handleGoalStatus = async (goal, status) => {
    const { status: progress } = status;
    await patch(`/goals/${goal?.id}`, status);
    if (progress === 'completed') {
      setGoals(
        goals.map((currentGoal) => (currentGoal.id === goal?.id ? { ...currentGoal, ...status } : currentGoal)),
      );
      const updatedGoal = { ...goal, ...status };
      handleGoalCelebration(updatedGoal);
    } else {
      setGoals(
        goals.map((currentGoal) => {
          if (currentGoal.id === goal?.id) {
            return { ...currentGoal, ...status };
          }
          return currentGoal;
        }),
      );
    }
  };

  const handleGoalVisibility = async (goal, visibility) => {
    const editedGoal = await patch(`/goals/${goal.id}`, { visibility });
    setGoals(
      goals.map((currentGoal) => {
        if (currentGoal.id === goal.id) {
          return editedGoal;
        }
        return currentGoal;
      }),
    );
  };

  const goToRelatedPost = (id) => {
    history.push(`/posts/${id}`);
  };

  useEffect(() => {
    handleFilteredGoals(selectedFilter);
  }, [selectedFilter, goals]);

  useEffect(() => {
    let cancelled = false;
    fetchGoals().then((response) => {
      if (cancelled) return;
      setGoals(response);
      setFilteredGoals(response);
      setInitialLoadComplete(true);
    });
    return () => { cancelled = true; };
  }, []);

  const handleFilterChange = (filter) => {
    const value = filter.value === 'all' ? null : filter.value;
    setSelectedFilter(value);
  };

  const showGoalAddBanner = !user?.isFlagged && userPermissions[CRUD_GOALS].allowed;

  if (initialLoadComplete && !goals.length) {
    return <NoGoals />;
  }

  return (
    <IonPage>
      <Header />
      <IonContent fullscreen>
        { loading || !initialLoadComplete ? (
          <LoadingSpinner />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'auto',
                mt: '15px',
                pb: '6px',
                ml: '24px',
                mr: '24px',
              }}
            >
              <FilterBar
                filterMeta={filters}
                onChangeCallBack={handleFilterChange}
                useFeedSelector={false}
                selectedValue={selectedFilter || 'all'}
              />
            </Box>
            <div className="goals-list">
              {filteredGoals?.map((goal) => (
                <Goal
                  key={goal.id}
                  goal={goal}
                  handleEdit={handleGoalEdit}
                  handleStatus={handleGoalStatus}
                  handleDelete={handleGoalDelete}
                  handleVisibility={handleGoalVisibility}
                  goToRelatedPost={goToRelatedPost}
                />
              ))}
            </div>
            {showGoalAddBanner ? (
              <Box mx={3}>
                <IonCard
                  className="goals-create-card-wrapper"
                  style={{ '--background': '#063d8f' }}
                >
                  <IonRouterLink routerLink="/goals-create">
                    <IonCardContent className="goals-create-card-content">
                      <div className="goals-create-card">
                        <img
                          className="goals-trophy"
                          src="/assets/goal-trophy.svg"
                          alt="goal trophy"
                        />
                        <div className="goals-create-card-message">
                          <Typography
                            variant="h2"
                            sx={{
                              position: 'relative',
                              color: '#fff',
                              fontSize: '1rem !important',
                              fontWeight: '700 !important',
                            }}
                          >
                            {strings.addNewGoal}
                            <IonIcon
                              style={{
                                position: 'relative',
                                top: '4px',
                                fontSize: '1.25rem',
                                marginLeft: '4px',
                              }}
                              src={arrowForwardOutline}
                            />
                          </Typography>
                          <Typography color="#fff">{strings.accomplishMore}</Typography>
                        </div>
                      </div>
                    </IonCardContent>
                  </IonRouterLink>
                </IonCard>
              </Box>
            ) : null}
          </motion.div>
        )}
      </IonContent>
      <Footer />
    </IonPage>
  );
};

Goals.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default Goals;
